import {
  AVERAGE_ROOM_RATE_CODE,
  BINARY_CONSTANTS,
  firstDayOfYear1900,
  PAYROLL_W2_CODE,
  VALIDATION_MESSAGES,
} from 'common/constants';
import { compareProductVersions, isValuePresent } from 'helpers/utils';
import { isArray, isEmpty, isObject } from 'lodash-es';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import { phoneNumberRegex } from './QuestionEngine.constants';
import type {
  AdditionalData,
  ExtendedQuestionEngineState,
  Field,
  ShowCondition,
  ShowConditionValue,
  ValidationObject,
} from './QuestionEngine.types';

/**
 * Year of the policy effective date, or `undefined` when it is missing/unparseable.
 * Accepts both the `Date` the pickers produce and the ISO string the API returns.
 */
const resolveEffectiveYear = (
  effectiveDate: ExtendedQuestionEngineState['effective_date'],
): number | undefined => {
  const dateTime =
    effectiveDate instanceof Date
      ? DateTime.fromJSDate(effectiveDate)
      : DateTime.fromISO(effectiveDate ?? '');

  return dateTime.isValid ? dateTime.year : undefined;
};

/**
 * Per-building age feeding the historic-registry show criteria
 * (`buildingAge > 49 and ...`, ENG-11626 / ENG-12242).
 * Returns `undefined` (never `NaN`, never an age derived from a missing effective
 * date) on invalid input — `NaN` slips past `isValuePresent`'s guard and makes
 * `!=` conditions wrongly true.
 */
export const computeBuildingAge = (
  state: ExtendedQuestionEngineState | undefined,
): number | undefined => {
  const yearBuilt = Number(state?.bdg_year_built);
  if (!Number.isInteger(yearBuilt) || yearBuilt <= 1000) return undefined;

  const effectiveYear = resolveEffectiveYear(state?.effective_date);
  if (effectiveYear === undefined) return undefined;

  return effectiveYear - yearBuilt;
};

/**
 * Check the question's show conditions and determine,
 * whether it should be shown or not.
 */
export const handleShowCondition = (field: Field, state: ExtendedQuestionEngineState) => {
  try {
    const effective_date = resolveEffectiveYear(state?.effective_date) ?? 0;

    /**
     * Calculate additional fields for show conditions,
     * Calculated aliases are dependant on other uw questions or policy data.
     * They should be updated whenever dependant data changes.
     * Also new calculated aliases should be defined and calculated below.
     * Initial task: https://dev.azure.com/radity-gmbh/THREE-insurance/_workitems/edit/15651/
     */
    const calculatedAliases = {
      yearsInBusiness: effective_date - (state?.yearFounded || 0),
      buildingAge: computeBuildingAge(state),
      /**
       * `pol_isrenewal` gates the new-business-only questions (the "loss runs required before bind"
       * agreements and `hasNewPropertyPurchaseOrTransfer`). CC stamps it onto policy_data at create; EC
       * deliberately does not carry the field — it has no product-config entry and exists only as the
       * rule-engine's `ECAliasCalculator.pol_isrenewal` alias, which never reaches this state. A missing
       * reference makes every operator return false, so those questions silently disappeared on the EC rail
       * (ENG-12114 CONGEN-002). The consumer flow only ever renders new business, so default it here; a
       * stored value still wins, which keeps CC's own field authoritative. `||` rather than `??` because an
       * empty string is what `isValuePresent` already rejects — present-but-blank has to default too.
       */
      pol_isrenewal: state?.pol_isrenewal || 'No',
      // Per-building, so it never reaches the policy-level premises state; the consumer flow stamps
      // every building `No` anyway (`PropertyModal`), so default it here (ENG-12240).
      isOutbuilding: state?.isOutbuilding || BINARY_CONSTANTS.NO,
    };

    const stateWithCalculatedAliases = { ...state, ...calculatedAliases };

    // Helper: handles __allow_blank logic for ALL value types
    // Returns:
    // - true  -> original field is blank or reference is not in state but marked as __allow_blank, so show the field
    // - false -> original field has value (must continue normal condition evaluation) or reference is not __allow_blank case

    // Retrieve the most deeply nested string reference
    const getDeepestReference = (
      reference: string | { reference?: any; [key: string]: any },
    ): string | undefined => {
      let current: any = reference;

      while (current && typeof current === 'object' && 'reference' in current) {
        current = current.reference;
      }

      return typeof current === 'string' ? current : undefined;
    };

    const checkAllowBlank = (
      reference: string | { reference?: any; [key: string]: any },
      derivedAliasesState: Record<string, any>,
    ): boolean => {
      const _reference = getDeepestReference(reference);

      if (!_reference || !_reference.endsWith('__allow_blank')) return false;

      const originalRef = _reference.replace('__allow_blank', '');

      if (!(originalRef in derivedAliasesState)) return true;

      const originalValue = derivedAliasesState[originalRef];

      return !isValuePresent(originalValue);
    };

    const handleCondition = {
      boolean: (reference: string, operator: string, value: ShowConditionValue) => {
        const allow = checkAllowBlank(reference, stateWithCalculatedAliases);
        if (allow === true) return true;

        const v = Boolean(value);
        const plainRef = stateWithCalculatedAliases[reference];
        const reff = Boolean(plainRef);

        const operators = {
          '==': () => reff === v,
          '!=': () => reff !== v,
        };

        return isValuePresent(plainRef) ? operators[operator]() : false;
      },

      string: (reference: string, operator: string, value: ShowConditionValue) => {
        const allow = checkAllowBlank(reference, stateWithCalculatedAliases);
        if (allow === true) return true;

        const plainRef = stateWithCalculatedAliases[reference];

        const values = Array.isArray(value) ? value : [value];

        const operators = {
          '**': () => values.some((v) => plainRef?.includes(v)),
          '!*': () => values.every((v) => !plainRef?.includes(v)),
          '==': () => values.some((v) => plainRef === v),
          '!=': () => values.every((v) => plainRef !== v),
        };

        return isValuePresent(plainRef) ? operators[operator]() : false;
      },

      number: (reference: string, operator: string, value: ShowConditionValue) => {
        const allow = checkAllowBlank(reference, stateWithCalculatedAliases);
        if (allow === true) return true;

        if (reference === 'productVersion') {
          const stateValue = stateWithCalculatedAliases[reference];
          if (!isValuePresent(stateValue)) return false;
          return compareProductVersions(stateValue, value as string | number, operator);
        }

        const v = +value;
        const plainRef = stateWithCalculatedAliases[reference];
        const reff = +plainRef;

        const operators = {
          '>': () => reff > v,
          '<': () => reff < v,
          '<=': () => reff <= v,
          '>=': () => reff >= v,
          '==': () => reff === v,
          '!=': () => reff !== v,
        };

        return isValuePresent(plainRef, [NaN]) ? operators[operator]() : false;
      },

      float: (
        reference: ShowCondition['reference'],
        operator: string,
        value: ShowConditionValue,
      ) => {
        const allow = checkAllowBlank(reference as any, stateWithCalculatedAliases);
        if (allow === true) return true;

        if (reference === 'productVersion') {
          const stateValue = stateWithCalculatedAliases[reference as string];
          if (!isValuePresent(stateValue)) return false;
          return compareProductVersions(stateValue, value as string | number, operator);
        }

        // value might be a reference or a value in different cases
        // if value is a reference then get the value of reference from the state object otherwise use the value
        const valueRef = stateWithCalculatedAliases[value as string];
        let v = valueRef ? parseFloat(valueRef) : parseFloat(value as string);

        // reference might be a reference or a value in different cases
        // if reference is a reference then get the value of reference from the state object otherwise use the reference as value
        const plainRef = stateWithCalculatedAliases[reference as string];
        let reff = plainRef ? parseFloat(plainRef) : parseFloat(reference as string);

        if (isObject(reference) || isObject(value)) {
          const tmpReference = reference as ShowCondition;

          // if there is an reference or value is recursive assume that there is an aritmetic operation
          const recursiveRef = tmpReference.reference
            ? handleCondition[tmpReference.value_type!](
                tmpReference.reference,
                tmpReference.relational_operator!,
                tmpReference.value!,
              )
            : reference;

          reff = parseFloat(recursiveRef);
        }

        if (isObject(value)) {
          const tmpValue = value as ShowCondition;

          const recursiveV = tmpValue.value
            ? handleCondition[tmpValue.value_type!](
                tmpValue.reference!,
                tmpValue.relational_operator!,
                tmpValue.value,
              )
            : value;

          v = parseFloat(recursiveV);
        }

        const operators = {
          '>': () => reff > v,
          '<': () => reff < v,
          '<=': () => reff <= v,
          '>=': () => reff >= v,
          '==': () => reff === v,
          '!=': () => reff !== v,

          // Artihmetic operators
          '+': () => reff + v,
          '-': () => reff - v,
          '*': () => reff * v,
          '/': () => reff / v,
          '%': () => reff % v,
        };

        return isValuePresent(reff, [NaN]) ? operators[operator]() : false;
      },

      array: (reference: string, operator: string, value: ShowConditionValue) => {
        const allow = checkAllowBlank(reference, stateWithCalculatedAliases);
        if (allow === true) return true;

        if (!isArray(value)) return false;

        const refValue = stateWithCalculatedAliases[reference];

        const reff =
          typeof refValue === 'string'
            ? refValue.split('|')
            : Array.isArray(refValue)
              ? refValue
              : [refValue ?? []];

        const operators = {
          in: () => value.some((v) => reff.includes(v)),
          '!in': () => value.every((v) => !reff.includes(v)),
        };

        return isValuePresent(reff) ? operators[operator]() : false;
      },
    };

    const checkShowCondition = (SC?: ShowCondition): boolean => {
      if (SC) {
        const isLogicalCondition = !isEmpty(SC.logical_operator);

        if (!isLogicalCondition) {
          return handleCondition[SC.value_type!](SC.reference!, SC.relational_operator!, SC.value!);
        } else {
          const { logical_operator: LO, operations } = SC;

          if (LO === 'and') {
            const results: boolean[] = operations?.map((condition) =>
              checkShowCondition(condition),
            ) ?? [true];

            return results.every((R) => R);
          } else if (LO === 'or') {
            const results: boolean[] = operations?.map((condition) =>
              checkShowCondition(condition),
            ) ?? [true];

            return results.some((R) => R);
          } else {
            return true;
          }
        }
      }

      return true;
    };

    const showField = checkShowCondition(field?.show_condition);

    return showField;
  } catch (_error) {
    // if there is an error checking the conditions do not show the field
    return false;
  }
};

/**
 * Applies the question set's `minLength` / `maxLength` character limits to a string schema.
 *
 * The limits live only in the question row's `additional_data` (no field code is hardcoded here),
 * mirroring the agent portal's `addRequiredValidationToDynamicFields` so both portals reject the
 * same values with the same wording. An empty value passes both tests — requiredness stays with
 * `is_optional`.
 */
const addLengthValidations = (
  schema: yup.StringSchema,
  additionalData?: AdditionalData,
): yup.StringSchema => {
  const { minLength, maxLength } = additionalData ?? {};

  if (minLength === undefined && maxLength === undefined) {
    return schema;
  }

  const isExactLength = minLength !== undefined && minLength === maxLength;
  let lengthSchema = schema.trim();

  if (minLength !== undefined) {
    lengthSchema = lengthSchema.test(
      'min-length',
      isExactLength
        ? `This field should be ${minLength} characters.`
        : `This field must be at least ${minLength} characters.`,
      (value) => (!value ? true : value.length >= minLength),
    );
  }

  if (maxLength !== undefined) {
    lengthSchema = lengthSchema.test(
      'max-length',
      isExactLength
        ? `This field should be ${maxLength} characters.`
        : `This field must be no more than ${maxLength} characters.`,
      (value) => (!value ? true : value.length <= maxLength),
    );
  }

  return lengthSchema;
};

/**
 * Map fields and add yup validations
 */
export const addValidationsToDynamicFields = (fields?: Field[]): ValidationObject => {
  const taxNumberFieldPattern = /(fein|ssn)/;

  return (
    // eslint-disable-next-line complexity
    fields?.reduce((acc, field) => {
      let validation: yup.StringSchema | yup.NumberSchema | yup.DateSchema;

      switch (field.type) {
        case 'phone': {
          validation = yup
            .string()
            .matches(phoneNumberRegex, VALIDATION_MESSAGES.invalidPhoneFormat);

          break;
        }

        case 'email': {
          validation = yup.string().email();

          break;
        }

        case 'year': {
          validation = yup.number();

          if (field?.additional_data?.disableFuture) {
            validation = validation.max(
              DateTime.now().year,
              `The year can't be bigger then the current year`,
            );
          }

          if (field?.additional_data?.disableDateBefore1900) {
            validation = validation.min(1900, `The year can't be less then 1900`);
          }

          if (field?.additional_data?.disableDateLessThan4Digits) {
            validation = validation.min(1000, `This field should be 4 digits.`);
          }

          break;
        }

        case 'date': {
          validation = yup
            .date()
            .typeError(VALIDATION_MESSAGES.invalidDateFormat)
            .min(firstDayOfYear1900, `The date must be after 1900`);

          if (field?.additional_data?.disableFuture) {
            validation = validation.max(
              DateTime.now().startOf('day').toJSDate(),
              `Date must be earlier than today`,
            );
          }

          break;
        }

        case 'number': {
          validation = yup.number();

          if (field?.additional_data?.min) {
            validation = yup
              .number()
              .min(
                Number(field?.additional_data?.min),
                `The value must be at least ${field?.additional_data?.min}.` as string,
              );
          }

          if (field?.additional_data?.max) {
            validation = validation.max(
              Number(field?.additional_data?.max),
              `The value must be no more than ${field?.additional_data?.max}.` as string,
            );
          }

          break;
        }

        case 'currency': {
          validation = yup.number();

          if (field?.additional_data?.min) {
            validation = yup
              .number()
              .min(
                Number(String(field?.additional_data?.min).replace(/,/g, '')),
                `The value must be at least $${field?.additional_data?.min}.` as string,
              );
          }

          if (field?.additional_data?.max) {
            validation = validation.max(
              Number(String(field?.additional_data?.max).replace(/,/g, '')),
              `The value must be no more than $${field?.additional_data?.max}.` as string,
            );
          }

          if (field?.code === AVERAGE_ROOM_RATE_CODE) {
            validation = yup
              .number()
              .max(
                Number(String(field?.additional_data?.max).replace(/,/g, '')),
                `Daily Room Rate exceeds $${field?.additional_data?.max} maximum allowed for rating. Please revise to continue.`,
              );
          }

          if (field?.code === PAYROLL_W2_CODE) {
            validation = yup
              .number()
              .max(
                50_000_000,
                'Payroll (W-2) exceeds $50,000,000 maximum. Please revise to continue.',
              );
          }

          break;
        }

        default: {
          validation = addLengthValidations(yup.string(), field.additional_data);

          break;
        }
      }

      if (taxNumberFieldPattern.test(field.code)) {
        validation = yup
          .string()
          .length(9)
          .test(field.code, VALIDATION_MESSAGES.dummyTaxIDValue, (val) => new Set(val).size > 1);
      }

      if (!field.is_optional) {
        validation = validation.required();
      }

      acc[field.code] = validation;

      return acc;
    }, {}) || {}
  );
};
